// Tableau 19 — Gestion financière : l'agent saisit les flux de base, tout le reste est calculé (théorique, total, écart).
import { SECTIONS_FINANCE, SECTION_LABELS, LIGNES_SECTION, CHAMPS_FINANCE, calculerFinance, controlerFinance, cleFinance,
  controlerLienFinanceInventaire, suggestionFinanceDepuisInventaire, montantFondsEquite } from '../../../../shared/business-logic/index.ts';
import type { SectionFinance } from '../../../../shared/business-logic/index.ts';
import { useModules } from '../../hooks/useModules.ts';
import { EnteteModule, NombreInput, PasDePeriode, Message, fmtAr } from '../../components/ui.tsx';
import type { Periode } from '../Accueil.tsx';

export default function Finance({ periode }: { periode: Periode | null }) {
  const m = useModules(periode);
  if (!periode) return <PasDePeriode />;
  if (!m.pret) return <p>Chargement…</p>;
  const calc = m.calc;
  if (!calc) return <Message niveau="warn">{m.erreur ?? 'Données indisponibles.'}</Message>;
  const brut = new Map(m.saisies('FINANCE').map((l) => [l.cle, l.donnees as Record<string, any>]));
  const inv = calc.inventaire.totaux;

  async function maj(section: SectionFinance, ligne: string, champ: string, v: number | string | null) {
    const cle = cleFinance(section, ligne);
    const suite = { ...(brut.get(cle) ?? {}), [champ]: v };
    const tout = Object.values(suite).every((x) => x === null || x === undefined || x === '');
    if (tout) await m.supprimer('FINANCE', cle); else await m.ecrire('FINANCE', cle, suite);
  }
  async function reprendreInventaire() {
    const s = suggestionFinanceDepuisInventaire(inv);
    const cle = cleFinance('MEDICAMENTS', 'Médicaments (Fanome)');
    await m.ecrire('FINANCE', cle, { ...(brut.get(cle) ?? {}), s1: s.s1, s3: s.s3, ph: s.ph });
  }

  return (
    <div>
      <EnteteModule titre="Tableau 19 — Gestion financière" periode={periode} statut={periode.statut} lectureSeule={m.lectureSeule}
        aide={<>Saisissez seulement les montants (en Ariary). Le <b>solde de début</b>, le <b>théorique</b>, le <b>total</b> et l'<b>écart</b> sont calculés.</>} />
      {SECTIONS_FINANCE.map((section) => (
        <section key={section} className="carte bloc-finance">
          <h2>{SECTION_LABELS[section]}</h2>
          {LIGNES_SECTION[section].map((ligne) => {
            const cle = cleFinance(section, ligne);
            const s = brut.get(cle) ?? {};
            const debutReporte = m.reports.financePhysique[cle] ?? null;
            const c = calculerFinance(section, s, debutReporte);
            const ctrl = controlerFinance(section, s, debutReporte);
            const lien = section === 'MEDICAMENTS' ? controlerLienFinanceInventaire(c.totalSorties, inv) : null;
            const groupes = [...new Set(CHAMPS_FINANCE[section].map((f) => f.groupe))];
            return (
              <div key={ligne} className="ligne-finance">
                <h3>{ligne}</h3>
                {groupes.map((g) => (
                  <fieldset key={g}>
                    <legend>{g}</legend>
                    <div className="champs">
                      {CHAMPS_FINANCE[section].filter((f) => f.groupe === g).map((f) => (
                        <label key={f.cle} className="champ-num">
                          <span>{f.label}</span>
                          <NombreInput label={`${ligne} — ${f.label}`} valeur={f.report ? (s[f.cle] ?? debutReporte) : s[f.cle]} disabled={m.lectureSeule}
                            classe={f.report && (s[f.cle] === null || s[f.cle] === undefined) && debutReporte !== null ? 'reporte' : ''}
                            titre={f.report ? 'Reporté du mois précédent — tapez pour remplacer' : undefined}
                            onChange={(v) => maj(section, ligne, f.cle, v)} />
                        </label>
                      ))}
                    </div>
                    {section === 'FANOME' && g.startsWith('Dépenses') && ligne === 'PhaGCom' && brut.get(cleFinance('FANOME', 'PhaGCom'))?.r1 ? (
                      <small className="note">Fonds d'équité à verser (3/135 de la vente) : <b>{fmtAr(montantFondsEquite(Number(brut.get(cleFinance('FANOME', 'PhaGCom'))?.r1)))}</b></small>
                    ) : null}
                  </fieldset>
                ))}
                <div className="resultats">
                  <div><span>Début</span><b>{fmtAr(c.debut)}</b></div>
                  <div><span>Total {section === 'MEDICAMENTS' ? 'entrées' : 'recettes'}</span><b>{fmtAr(c.totalEntrees)}</b></div>
                  <div><span>Total {section === 'MEDICAMENTS' ? 'sorties' : 'dépenses'}</span><b>{fmtAr(c.totalSorties)}</b></div>
                  <div><span>{section === 'MEDICAMENTS' ? 'Valeur théorique fin' : 'Solde théorique fin'}</span><b>{fmtAr(c.theorique)}</b></div>
                  <div><span>{section === 'MEDICAMENTS' ? 'Valeur physique fin' : 'Solde physique (total)'}</span><b>{fmtAr(c.physique)}</b></div>
                  <div className={c.ecart === 0 ? 'ok' : 'bad'}><span>Écart (physique − théorique)</span><b>{fmtAr(c.ecart)}</b></div>
                </div>
                {ctrl && ctrl.niveau !== 'ok' && <Message niveau={ctrl.niveau}>{ctrl.message}</Message>}
                {section === 'MEDICAMENTS' && (
                  <div className="lien-inventaire">
                    {inv.nbComptes > 0 ? (
                      <>
                        <Message niveau={lien?.niveau === 'ok' ? 'ok' : 'warn'}>
                          🔗 Inventaire : dispensation {fmtAr(inv.valeurDispensation)} + périmés/avariés {fmtAr(inv.valeurPerime)} = <b>{fmtAr(inv.sortiesValeur)}</b> de sorties attendues. {lien?.message}
                        </Message>
                        {!m.lectureSeule && lien?.niveau !== 'ok' && <button className="secondaire" onClick={reprendreInventaire}>Reprendre les montants de l'inventaire</button>}
                      </>
                    ) : <small className="note">Quand l'inventaire du mois sera compté, le total des sorties sera comparé automatiquement à la dispensation + périmés.</small>}
                  </div>
                )}
                <label className="champ-texte"><span>Observation</span>
                  <input value={s.ob ?? ''} maxLength={300} disabled={m.lectureSeule} onChange={(e) => maj(section, ligne, 'ob', e.target.value)} />
                </label>
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}
